// Drives the generic background-LLM poll. While `active` (something the user is watching is
// 'processing'), it POSTs /v1/llm-jobs/check for the org on an interval so any in-flight
// background run (discover-partners, strategic-goals, account-news, ...) is retrieved and
// finalized. The endpoint is idempotent and org-scoped; each tick drains all in-flight jobs.
// Mirrors how IntelligenceTab drives /v1/competitor-research/check for the competitor flow.
//
// This does NOT read status itself -- the caller keeps its own status poll (useRealtimeStatus
// / overview-status). This hook only nudges the backend to advance the jobs; the domain row
// each flow writes on finalize is what the caller's poll then observes.
import { useEffect, useRef } from 'react';
import { useApi } from './useApi';
import { useTabVisible } from './useTabVisibility';

const DEFAULT_INTERVAL_MS = 5000;

// Each tenant lambda owns its background flows and exposes its own check endpoint (the
// llm_jobs table is shared, but a lambda only finalizes its own flows). Pass the `path` for
// the lambda whose flow the mounting component is watching. Defaults to account-management.
export function useLlmJobsPoll(
  orgId: string | undefined,
  active: boolean,
  path: string = '/v1/llm-jobs/check',
  intervalMs: number = DEFAULT_INTERVAL_MS,
): void {
  const { apiPost } = useApi();
  const tabVisible = useTabVisible();
  const apiPostRef = useRef(apiPost);
  apiPostRef.current = apiPost;

  useEffect(() => {
    if (!orgId || !active || !tabVisible) return;

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      // Fire-and-forget: a failed tick is retried on the next interval.
      apiPostRef.current(path, { org_id: orgId }).catch(() => { /* next tick retries */ });
    };

    tick();
    const timer = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [orgId, active, path, intervalMs, tabVisible]);
}
